import { useCallback, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Drawer } from "vaul";
import { MessageSquare, Send, X } from "lucide-react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, extractApiError } from "../lib/api";
import { fetchEntities } from "../lib/dataFetchers";
import type { AgentResponse, ChatMessage, EntityChip } from "../lib/types";
import { useChatHistory, type StoredMessage } from "../lib/chatHistory";
import { cn } from "../lib/cn";
import { TypingDots } from "./TypingDots";

interface Props {
  open: boolean;
  onClose: () => void;
  selectedCountry?: string;
}

function Panel({ selectedCountry }: { selectedCountry?: string }): JSX.Element {
  const { t } = useTranslation("chat");
  // Live entity count — shared cache with Home (["entities-all"]); stays correct
  // on entity create/delete/restore (those mutations invalidate this key).
  const { data: entitiesRes } = useQuery({
    queryKey: ["entities-all"],
    queryFn: () => fetchEntities({ limit: 100 }),
    staleTime: 60_000,
  });
  const entityCount = entitiesRes?.total ?? entitiesRes?.entities.length;
  const [messages, setMessages] = useChatHistory();
  const hasUserMessage = messages.some((m) => m.role === "user");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const suggestions = useMemo(
    () => [
      t("sidebar_suggest_europe_renewals"),
      t("sidebar_suggest_ubo_lookup"),
      t("sidebar_suggest_compare_latin_america"),
      t("sidebar_suggest_ubo_summary"),
    ],
    [t],
  );

  // ── Send handler — ALL existing logic preserved exactly ──────────────────
  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || busy) return;
      const userMsg: StoredMessage = { id: `u-${Date.now()}`, role: "user", content: trimmed };
      setMessages((m) => [...m, userMsg]);
      setInput("");
      setBusy(true);
      try {
        const wire: ChatMessage[] = messages
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m) => ({ role: m.role, content: m.content }))
          .concat({ role: "user", content: trimmed });
        const res = await api.post<AgentResponse>("/agent", {
          messages: wire,
          selectedCountry,
        });
        const data = res.data;
        setMessages((m) => [
          ...m,
          {
            id: `a-${Date.now()}`,
            role: "assistant",
            content: data.answerText,
            chips: data.entities,
          },
        ]);
      } catch (err) {
        const ax = err as import("axios").AxiosError;
        if (ax.response?.status === 429) {
          const retryHeader = ax.response.headers["retry-after"] as string | undefined;
          const seconds = typeof retryHeader === "string" ? parseInt(retryHeader, 10) : NaN;
          const n = isNaN(seconds) ? 30 : seconds;
          toast.error(t("rate_limited", { seconds: n }));
        } else {
          toast.error(extractApiError(err));
        }
        setMessages((m) => [
          ...m,
          {
            id: `a-${Date.now()}`,
            role: "assistant",
            content: t("agent_unavailable"),
          },
        ]);
      } finally {
        setBusy(false);
        requestAnimationFrame(() => {
          scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
        });
      }
    },
    [busy, messages, selectedCountry, setMessages, t],
  );

  return (
    // Proto .chat container
    <div className="flex h-full flex-col bg-surface">
      {/* Proto .chat-h header */}
      <header
        className={cn(
          "sticky top-0 z-10 flex items-center gap-[9px] px-[15px] py-[14px]",
          "border-b border-border-2 bg-surface shrink-0",
        )}
      >
        {/* Proto agent avatar: accent-soft bg, accent icon */}
        <span
          aria-hidden
          className="w-7 h-7 rounded-[8px] bg-accent-soft text-accent flex items-center justify-center shrink-0"
        >
          <MessageSquare className="w-3.5 h-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          {/* Proto name: font-bold text-[13px] */}
          <div className="font-bold text-[13px] text-text leading-none truncate">
            Hightekers Agent
          </div>
          {/* Proto "always on" indicator: text-[10.5px] text-green-text */}
          <div className="text-[10.5px] text-green-text leading-none mt-0.5">
            {t("sidebar_status_always_on")}
          </div>
        </div>
        {/* Proto expand link: ml-auto text-[11.5px] text-accent font-semibold */}
        <Link
          to="/chat"
          className="ml-auto text-[11.5px] text-accent font-semibold hover:underline shrink-0"
        >
          {t("sidebar_expand")}
        </Link>
      </header>

      {/* Proto .chat-body — scrollable message area */}
      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto p-[15px] space-y-3 queue-scroll"
      >
        {messages.map((m) =>
          m.role === "user" ? (
            // User bubble — accent bg, right-aligned
            <div key={m.id} className="flex justify-end">
              <div className="max-w-[85%] bg-accent text-white text-[12.5px] leading-relaxed rounded-2xl rounded-tr-sm px-3 py-2">
                {m.content}
              </div>
            </div>
          ) : (
            // Assistant bubble — proto .bubble
            <div key={m.id} className="flex justify-start">
              <div className="max-w-[90%]">
                <div
                  className={cn(
                    // Proto .bubble
                    "bg-surface-2 border border-border-2 rounded-[12px] rounded-tl-[4px]",
                    "p-[12px_14px] text-[12.5px] leading-relaxed text-text whitespace-pre-wrap",
                  )}
                >
                  {m.id === "seed"
                    ? t("seed_message", { count: entityCount ?? "" })
                    : m.content}
                </div>
                {m.chips && m.chips.length > 0 && (
                  <div className="mt-2">
                    <div className="text-[10px] uppercase tracking-wide text-dim font-semibold mb-1">
                      {t("referenced_entities")}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(m.chips as EntityChip[]).map((c) => (
                        <a
                          key={c.id}
                          href={`/entities/${c.id}`}
                          className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-accent-soft text-accent border border-[#c7d2fe] hover:bg-[#e0e7ff] transition-colors"
                        >
                          {c.flag && <span aria-hidden>{c.flag}</span>}
                          <span>{c.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ),
        )}
        {busy && <TypingDots />}
      </div>

      {/* Proto "Try asking" suggestions — shown before first user message */}
      {!hasUserMessage && (
        <div className="px-[15px] pt-0 pb-2 border-t border-border-2 shrink-0">
          {/* Proto label: text-[10px] font-bold tracking-[.06em] uppercase text-dim mt-5 mb-[9px] */}
          <p className="text-[10px] font-bold tracking-[.06em] uppercase text-dim mt-5 mb-[9px]">
            {t("sidebar_try_asking")}
          </p>
          <div className="flex flex-col gap-1.5">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => void send(s)}
                disabled={busy}
                className={cn(
                  // Proto suggestion button
                  "text-left bg-surface border border-border rounded-[9px]",
                  "p-[9px_12px] text-[12px] text-text",
                  "hover:border-accent hover:bg-accent-tint transition-colors",
                  "disabled:opacity-50",
                )}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Proto .chat-foot — input footer */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void send(input);
        }}
        className="sticky bottom-0 px-[15px] py-[10px] border-t border-border bg-surface shrink-0 safe-bottom"
      >
        {/* Proto footer input container */}
        <div
          className={cn(
            "border border-border rounded-[10px] p-[6px_6px_6px_12px]",
            "bg-surface-2 flex items-center gap-[7px]",
          )}
        >
          <label htmlFor="agent-input-sidebar" className="sr-only">
            Ask the AI assistant
          </label>
          <input
            id="agent-input-sidebar"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            type="text"
            placeholder={t("input_placeholder")}
            autoComplete="off"
            // Proto: flex-1 border-none bg-transparent text-[13px] outline-none
            className="flex-1 border-0 bg-transparent text-[13px] text-text placeholder:text-dim outline-none"
          />
          {/* Proto send button: w-8 h-8 rounded-[8px] bg-accent text-white */}
          <button
            type="submit"
            aria-label={t("sidebar_send_aria")}
            disabled={busy || !input.trim()}
            className={cn(
              "w-8 h-8 rounded-[8px] bg-accent text-white",
              "flex items-center justify-center",
              "hover:bg-[#4338ca] transition-colors",
              "disabled:opacity-50",
            )}
          >
            <Send className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
        <p className="text-[10px] text-dim mt-1.5 text-right">
          <span className="kbd">⌘+Enter</span> {t("sidebar_cmd_enter")}
        </p>
      </form>
    </div>
  );
}

export function AIAssistantSidebar({ open, onClose, selectedCountry }: Props): JSX.Element {
  const { t } = useTranslation("chat");
  return (
    <>
      {/* Desktop rail — proto right panel: border-l border-border bg-surface flex flex-col */}
      <aside
        aria-label={t("sidebar_assistant_aria")}
        className={cn(
          "hidden lg:flex shrink-0 bg-surface border-l border-border",
          // Proto: 300px (not previous w-64 = 256px)
          "w-[300px] h-[calc(100vh-3.5rem)] sticky top-14 flex-col min-h-0",
        )}
      >
        <Panel selectedCountry={selectedCountry} />
      </aside>

      {/* Mobile bottom sheet — behaviour unchanged */}
      <Drawer.Root open={open} onOpenChange={(v) => !v && onClose()}>
        <Drawer.Portal>
          <Drawer.Overlay className="lg:hidden fixed inset-0 z-40 bg-black/35" />
          <Drawer.Content
            aria-label={t("sidebar_assistant_aria")}
            className={cn(
              "lg:hidden fixed bottom-0 left-0 right-0 z-50",
              "h-[90vh] rounded-t-2xl bg-surface border-t border-border focus:outline-none",
              "flex flex-col",
            )}
          >
            <div className="mx-auto mt-2 mb-1 h-1.5 w-12 rounded-full bg-border" aria-hidden />
            <div className="flex items-center justify-between px-4 pb-2 shrink-0">
              <Drawer.Title className="text-[13px] font-bold">Hightekers Agent</Drawer.Title>
              <button
                type="button"
                onClick={onClose}
                aria-label={t("sidebar_close_aria")}
                className="h-11 w-11 inline-flex items-center justify-center rounded-lg hover:bg-surface-2 text-muted"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <Panel selectedCountry={selectedCountry} />
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </>
  );
}
